/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as projectMembersApi from '@/lib/api/projectMembers'
import type { ProjectRole } from '@/types/project-teams'

/**
 * Hook to fetch all members of a project
 */
export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projectMembers', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')
      return projectMembersApi.getProjectMembers(projectId)
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to fetch current user's membership in a project
 */
export function useProjectMembership(projectId: string | undefined) {
  return useQuery({
    queryKey: ['projectMembership', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')
      return projectMembersApi.getProjectMembership(projectId)
    },
    enabled: !!projectId,
  })
}

/**
 * Hook to add a member to a project
 */
export function useAddProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: projectMembersApi.addProjectMember,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectMembers', variables.project_id] })
    },
  })
}

/**
 * Hook to update a member's role
 */
export function useUpdateProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      userId,
      role,
    }: {
      projectId: string
      userId: string
      role: ProjectRole
    }) => projectMembersApi.updateProjectMember(projectId, userId, { role }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectMembers', variables.projectId] })
    },
  })
}

/**
 * Hook to remove a member from a project
 */
export function useRemoveProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) =>
      projectMembersApi.removeProjectMember(projectId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projectMembers', variables.projectId] })
    },
  })
}

/**
 * Hook to check if user is project admin
 */
export function useIsProjectAdmin(projectId: string | undefined, userId?: string) {
  return useQuery({
    queryKey: ['isProjectAdmin', projectId, userId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required')
      return projectMembersApi.isProjectAdmin(projectId, userId)
    },
    enabled: !!projectId,
  })
}
